import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { type ContentMedia } from './content-media';
import { MEDIA_CONFIG } from './media.config';

/** What the Media tab is attaching, derived from the item's post type. */
export type MediaKind = 'image' | 'video' | 'document';

/**
 * Cloudinary's own two upload endpoints. Documents ride `image` — that is how
 * Cloudinary rasterises a PDF and reports its `pages`, and it is what legacy
 * does (`uploadDocument` omits the resource-type argument, so it defaults to
 * `'image'` — cloudinary.service.ts:129-167 / :172). The API's `SignUploadDto`
 * only accepts these two values anyway (sign-upload.dto.ts:3).
 */
export type UploadResourceType = 'image' | 'video';

/**
 * The upload preset every creator-content upload uses
 * (crud-content.component.ts:474-481 passes `'users-media'` on all three paths).
 */
export const CONTENT_UPLOAD_PRESET = 'users-media';

/**
 * The API path that mints an upload signature, relative to the app's `apiUrl`.
 *
 * Shared with the users app's `CloudinaryService.signUpload`, which posts to the
 * same endpoint: the two upload paths must agree on it, and a constant is the
 * only way to make a rename break both at once rather than one silently.
 */
export const SIGN_UPLOAD_PATH = '/external-media/cloudinary/sign-upload';

/**
 * The signature the API mints — `SignedUploadResponse`
 * (ts-api apps/sisoma-api/src/_services/external-media/dto/sign-upload.dto.ts:21-29).
 *
 * `publicId` is **server-generated** (13 alphanumerics) and `tags` is the
 * constant `'browser_upload'`; both are covered by the signature, so they must
 * be posted back verbatim.
 */
export interface CloudinarySignature {
  cloudName: string;
  apiKey: string;
  timestamp: number;
  publicId: string;
  tags: string;
  uploadPreset: string;
  signature: string;
}

/** The fields the mapper reads off Cloudinary's upload response. */
export interface CloudinaryUploadResponse {
  secure_url: string;
  public_id: string;
  width?: number;
  height?: number;
  format?: string;
  pages?: number;
}

/**
 * Direct-to-Cloudinary uploads for the content drawer's Media tab — the lean
 * partner-area port of `projects/users/.../_shared/services/cloudinary.service.ts`.
 *
 * The legacy service cannot be imported (eslint forbids partner-area reaching
 * into `projects/users`, and that tree compiles non-strict), so the two things
 * that are actually a contract are reproduced exactly:
 *
 * 1. **Sign** — `POST {apiUrl}/external-media/cloudinary/sign-upload` with
 *    `{ resourceType, uploadPreset }` (cloudinary.service.ts:226-236). No `/v1`:
 *    `ExternalMediaController` declares no version and the API sets no default
 *    version. The endpoint is authenticated but otherwise ungated — the global
 *    `AuthenticationGuard` defaults to Bearer and the controller adds no roles or
 *    capabilities — so any signed-in partner user can call it. The shared
 *    `authInterceptor` supplies the cookie and `creatorSub` because the URL is
 *    under `apiUrl`.
 * 2. **Upload** — the seven-key signed `FormData` straight to
 *    `https://api.cloudinary.com/v1_1/{cloudName}/{image|video}/upload`
 *    (cloudinary.service.ts:238-254). The signed param set is exactly
 *    `{ public_id, tags, timestamp, upload_preset }`
 *    (ts-api cloudinary-signing.service.ts:39-46), so **adding any field to the
 *    form breaks the signature**. The Cloudinary host is outside `apiUrl`, so the
 *    auth interceptor skips it and no cookie or partner header leaks.
 *
 * ## What is deliberately *not* ported
 *
 * - **Upload progress.** The legacy service asks for `reportProgress: true`, but
 *   partner-area's `provideAuth` installs `provideHttpClient(withFetch(), …)` and
 *   Angular's fetch backend never emits `HttpEventType.UploadProgress` (only
 *   `DownloadProgress`). A ported progress bar would sit at 0% in the real app
 *   while passing green in tests, because `HttpTestingController` lets a spec
 *   synthesise the event by hand. The Media tab shows an indeterminate spinner
 *   card instead — which is what the legacy *screens* effectively show anyway
 *   (they drive `startProgress()`, a fake 1→100 ticker; nothing subscribes to the
 *   service's real `isLoading`).
 * - The shared-mutable `loadingData` object, which concurrent uploads clobber.
 *
 * ## Uploads are filed in the acting user's media library
 *
 * Every successful upload and import is also POSTed to `{apiUrl}/user-media-library`
 * — legacy's `addMediaToUser` (cloudinary.service.ts:257-261) — so the picker's
 * **My Media** tab shows what this drawer put there. Fire-and-forget, exactly as
 * legacy calls it: `addMediaToUser(image)` is never awaited (cloudinary.service.ts:79,
 * :121, :163), so the attach must not fail because the filing did.
 *
 * Legacy's creator-content screen is inconsistent about this and the split is a
 * bug, not a rule. `uploadProcess` — the one function that carries all three
 * branches — sets `saveToUser = true` and passes it to every one
 * (crud-content.component.ts:458-483). But `addMedia` never reaches that
 * function for an image: it detours through `uploadImageToCloudinary`
 * (crud-content.component.ts:350 → :594-596), which hard-codes
 * `saveToUser = false` **and** signs with the creator's uuid in the upload-preset
 * slot. So in the shipped app a creator's content videos and documents appear in
 * My Media and their images do not — from the same call site that cannot succeed
 * at Cloudinary in the first place. The rule as written is followed here.
 */
@Injectable()
export class CloudinaryUploadService {
  private readonly http = inject(HttpClient);
  private readonly api = inject(MEDIA_CONFIG).apiUrl;

  /**
   * Upload one file and return it in the shape the content API stores.
   *
   * Rejects with whatever the sign or upload call rejected — the caller decides
   * what the user sees.
   */
  async upload(file: File, kind: MediaKind): Promise<ContentMedia> {
    const resourceType: UploadResourceType = kind === 'video' ? 'video' : 'image';
    const signature = await this.sign(resourceType);

    const uploaded = await firstValueFrom(
      this.http.post<CloudinaryUploadResponse>(
        uploadUrl(signature.cloudName, resourceType),
        signedFormData(file, signature),
      ),
    );

    const media = toContentMedia(uploaded, kind, file);
    this.file(media);
    return media;
  }

  /**
   * Import a stock photo by URL and return it in the shape the content API
   * stores — the picker's Pexels / UnSplash / Pixabay tabs.
   *
   * This is the same signed upload, with the provider's URL in the `file` field
   * instead of a `File`. Cloudinary fetches it server-side, so the asset ends up
   * ours: a Cloudinary `secure_url` and a Cloudinary `public_id`, which is what
   * the content node's `mediaPublicId` has to be. Legacy takes the identical
   * route without ever saying so — `uploadMediaToCloudinary`'s
   * `const file = mediaFile instanceof Blob ? mediaFile : mediaFile?.url`
   * (cloudinary.service.ts:176) is the whole mechanism, reached from
   * `crud-content`'s `addMedia` because a stock item's url fails the
   * `.includes('cloudinary')` test (crud-content.component.ts:419-423).
   *
   * Two things legacy does on that path are **not** reproduced, both reported:
   *
   * 1. It signs with `this.creator().id` as the upload preset
   *    (crud-content.component.ts:350 → :594-596), i.e. a creator uuid where a
   *    Cloudinary preset name belongs. The API signs whatever string it is given
   *    (`SignUploadDto` only checks the character class), and Cloudinary then
   *    rejects the upload with "Upload preset not found" — so that call is not a
   *    contract to copy. `uploadProcess`'s own three branches all pass
   *    `'users-media'` (crud-content.component.ts:473-482), which is what
   *    {@link CONTENT_UPLOAD_PRESET} already is.
   * 2. The same call passes `png = false`, which rewrites the stored url's
   *    extension to `.jpeg` (cloudinary.service.ts:67-69). Skipped: it rides on
   *    the broken call above, it would give the two halves of one media list two
   *    different url conventions (device uploads here keep their extension), and
   *    on a PNG it silently drops the alpha channel.
   */
  async importUrl(url: string, kind: MediaKind): Promise<ContentMedia> {
    const resourceType: UploadResourceType = kind === 'video' ? 'video' : 'image';
    const signature = await this.sign(resourceType);

    const uploaded = await firstValueFrom(
      this.http.post<CloudinaryUploadResponse>(
        uploadUrl(signature.cloudName, resourceType),
        signedFormData(url, signature),
      ),
    );

    const media = toContentMedia(uploaded, kind);
    this.file(media);
    return media;
  }

  private async sign(resourceType: UploadResourceType): Promise<CloudinarySignature> {
    return firstValueFrom(
      this.http.post<CloudinarySignature>(`${this.api}${SIGN_UPLOAD_PATH}`, {
        resourceType,
        uploadPreset: CONTENT_UPLOAD_PRESET,
      }),
    );
  }

  /**
   * File the asset in the acting user's media library so the picker's My Media
   * tab can find it again — see the class doc.
   *
   * Three keys, because three is all the producer keeps: `MediaLibraryService.create`
   * destructures `{ url, mediaPublicId, type }` off the body and writes those plus
   * its own `createdOn` (media-library.service.ts:20-42). `type` matters — the
   * list's `category` filter is `WHERE toLower(doc.type) = …`.
   *
   * Not awaited and never rethrown: the media is already attached by the time
   * this runs, and a failed filing is not a failed upload.
   */
  private file(media: ContentMedia): void {
    if (!media.url || !media.mediaPublicId) return;

    firstValueFrom(
      this.http.post(`${this.api}/user-media-library`, {
        url: media.url,
        mediaPublicId: media.mediaPublicId,
        type: media.type,
      }),
    ).catch(() => undefined);
  }
}

/** cloudinary.service.ts:251-254. The cloud name comes from the signature, never the caller. */
export function uploadUrl(cloudName: string, resourceType: UploadResourceType): string {
  const segment = resourceType === 'video' ? 'video/upload' : 'image/upload';
  return `https://api.cloudinary.com/v1_1/${cloudName}/${segment}`;
}

/**
 * The signed multipart body — seven keys, every value from the signature
 * (cloudinary.service.ts:238-249). Nothing else may be added: the server signs
 * `{ public_id, tags, timestamp, upload_preset }`, so an extra signed-eligible
 * field makes Cloudinary reject the upload.
 *
 * `file` is a `File` (or any `Blob` — the users app posts a canvas render as
 * one) for a device upload and a **remote URL string** for a stock import.
 * Cloudinary's `file` field accepts either, and the users app relies on the
 * same overload (cloudinary.service.ts:176).
 */
export function signedFormData(file: Blob | string, signature: CloudinarySignature): FormData {
  const form = new FormData();
  form.append('file', file);
  form.append('api_key', signature.apiKey);
  form.append('timestamp', String(signature.timestamp));
  form.append('public_id', signature.publicId);
  form.append('upload_preset', signature.uploadPreset);
  form.append('tags', signature.tags);
  form.append('signature', signature.signature);
  return form;
}

/**
 * Cloudinary's response → the media element the content API stores.
 *
 * Pinned to the **real producer** for this flow, which is `media-upload.service`'s
 * `handleImageFlow` / `handleVideosFlow` / `handleDocumentsFlow`
 * (projects/users/.../\_shared/services/media-upload.service.ts:328-378, :657-684)
 * — not `cloudinary.service`'s own `uploadImage`. The creator modal uploads
 * through the former and then `crud-content`'s `uploadProcess` short-circuits on
 * `source === 'usersMedia'` (crud-content.component.ts:443-455), so the object
 * the modal built is what reaches the payload byte-for-byte: the `media` control
 * is a plain `FormControl` holding the raw array, with no mapper or whitelist.
 *
 * Three legacy fields are deliberately dropped:
 *
 * - **`id`** — legacy sets it to the *file name*
 *   (`media-upload.service.ts:672`). The API MERGEs the node on
 *   `{id: coalesce(mediaItem.mediaPublicId, randomUUID())}` and then runs
 *   `SET m += mediaItem` (ts-api content.service.ts:461-469), so a file-name `id`
 *   overwrites the merge key: two files called `photo.jpg` end up as two nodes
 *   sharing one id, and the update diff's `handleDeletingMedia` matches on that
 *   id and would remove both. Omitting `id` keeps `node.id === mediaPublicId`,
 *   which is what the diff (content.service.ts:257-262) and
 *   `handleOriginalMediaIds` assume.
 * - **`thumbUrl`** — a duplicate of `url` that nothing in this app reads.
 * - **`source: 'usersMedia'`** — a legacy routing flag for `uploadProcess`'s
 *   "already uploaded, skip" branch. There is no such branch here.
 *
 * Everything else is legacy's, including the two URL rewrites (there are no
 * `f_auto` / `q_auto` / `.webp` transforms on this path, and the `pg_N` PDF
 * rasterisation is display-only — legacy's `create-array` pipe — never stored):
 *
 * - a video's `previewUrl` is `url.replace('.mp4', '.jpg')` (cloudinary.service.ts:117);
 * - a document's `previewUrl` swaps the extension for `.jpeg`
 *   (cloudinary.service.ts:139-144) — Cloudinary delivers the PDF's first page there.
 *
 * An image's `url` is **not** rewritten to `.jpeg`. That rewrite lives in
 * `uploadImage` behind a `png` flag, and this flow passes `png = true`
 * (media-upload.service.ts:260-265), so the stored URL keeps its original
 * extension.
 */
export function toContentMedia(
  uploaded: CloudinaryUploadResponse,
  kind: MediaKind,
  /** The device file, when there was one. A stock import has no local file. */
  file?: File,
): ContentMedia {
  const url = uploaded.secure_url;
  const base = { url, mediaPublicId: uploaded.public_id };
  const size =
    uploaded.width && uploaded.height ? { width: uploaded.width, height: uploaded.height } : {};

  if (kind === 'video') {
    return { ...base, ...size, type: 'video', previewUrl: url.replace('.mp4', '.jpg') };
  }

  if (kind === 'document') {
    return {
      ...base,
      type: 'document',
      previewUrl: posterUrl(url),
      ...(file ? { documentType: file.type, documentName: file.name } : {}),
      ...(uploaded.pages ? { pages: uploaded.pages } : {}),
    };
  }

  return { ...base, ...size, type: 'image' };
}

/** cloudinary.service.ts:139-144 — the extension swapped for `.jpeg`. */
export function posterUrl(url: string): string {
  const extension = url.split(/[. ]+/).pop();
  return !extension || extension === 'jpeg' ? url : url.replace(`.${extension}`, '.jpeg');
}
